import { useEffect, useMemo, useState } from 'react';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { isChairOf } from './helpers';
import { isTableItem, type CanvasItem } from './types';

export interface BulkEditValues {
  objectType: string;
  widthFt: number;
  heightFt: number;
  seats: number;
  chairSizeFt: number;
  /** New label for every matching object, or undefined to leave labels alone. */
  label?: string;
  alwaysPlaceChairs: boolean;
}

interface BulkEditObjectsModalProps {
  open: boolean;
  onClose: () => void;
  items: CanvasItem[];
  defaultChairSizeFt: number;
  onApply: (values: BulkEditValues) => void;
}

/** The object types on the plan worth bulk-editing (chairs follow their tables). */
export function bulkEditTypesOf(items: CanvasItem[]): string[] {
  return [
    ...new Set(items.filter((it) => it.objectType !== 'CHAIR').map((it) => it.objectType)),
  ].sort();
}

const field = 'w-full border border-gray-300 rounded px-2 py-1.5 text-sm';
const label = 'block text-sm font-medium text-gray-700 mb-1';

/** Resize, re-seat and relabel every object of one type at once. */
export function BulkEditObjectsModal({
  open,
  onClose,
  items,
  defaultChairSizeFt,
  onApply,
}: BulkEditObjectsModalProps) {
  const types = useMemo(() => bulkEditTypesOf(items), [items]);
  const [objectType, setObjectType] = useState('');
  const [widthFt, setWidthFt] = useState(5);
  const [heightFt, setHeightFt] = useState(5);
  const [seats, setSeats] = useState(8);
  const [chairSizeFt, setChairSizeFt] = useState(defaultChairSizeFt);
  const [changeLabel, setChangeLabel] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [alwaysPlaceChairs, setAlwaysPlaceChairs] = useState(false);

  // Seed from a representative object of the chosen type.
  const chooseType = (type: string) => {
    setObjectType(type);
    const rep = items.find((it) => it.objectType === type);
    if (rep) {
      setWidthFt(rep.widthFt);
      setHeightFt(rep.heightFt);
      setSeats(rep.seats ?? 0);
    }
  };

  useEffect(() => {
    if (!open) return;
    setChairSizeFt(defaultChairSizeFt);
    if (types[0]) chooseType(types[0]);
    // Only on opening: re-seeding on every canvas change would undo typing.
  }, [open]);

  const matching = items.filter((it) => it.objectType === objectType);
  const tables = matching.filter((it) => isTableItem(it));
  const withChairs = tables.filter((t) => items.some((ch) => isChairOf(ch, t))).length;

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title="Bulk Edit Objects"
      size="xs"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 py-2 border border-gray-300 rounded text-sm text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() =>
              objectType &&
              onApply({
                objectType,
                widthFt,
                heightFt,
                seats,
                chairSizeFt,
                label: changeLabel && newLabel.trim() ? newLabel.trim() : undefined,
                alwaysPlaceChairs,
              })
            }
            className="flex-1 py-2 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
            data-testid="bulk-edit-apply-btn"
          >
            Apply to All
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <span className={label}>Object Type</span>
          <SearchableListSelect<string>
            items={types}
            value={objectType || null}
            onChange={(v) => {
              if (typeof v === 'string' && v) chooseType(v);
            }}
            getValue={(type) => type}
            getLabel={(type) => {
              const count = items.filter((it) => it.objectType === type).length;
              return `${type} (${count} object${count !== 1 ? 's' : ''})`;
            }}
            clearable={false}
            ariaLabel="Object type"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={label} htmlFor="bulk-edit-width">
              Width (ft)
            </label>
            <input
              id="bulk-edit-width"
              type="number"
              min={0.5}
              step={0.5}
              value={widthFt}
              onChange={(e) => setWidthFt(parseFloat(e.target.value) || 1)}
              className={field}
              data-testid="bulk-edit-width"
            />
          </div>
          <div>
            <label className={label} htmlFor="bulk-edit-height">
              Height (ft)
            </label>
            <input
              id="bulk-edit-height"
              type="number"
              min={0.5}
              step={0.5}
              value={heightFt}
              onChange={(e) => setHeightFt(parseFloat(e.target.value) || 1)}
              className={field}
              data-testid="bulk-edit-height"
            />
          </div>
        </div>

        <div>
          <label className={label} htmlFor="bulk-edit-seats">
            Seats
          </label>
          <input
            id="bulk-edit-seats"
            type="number"
            min={0}
            step={1}
            value={seats}
            onChange={(e) => setSeats(parseInt(e.target.value, 10) || 0)}
            className={field}
            data-testid="bulk-edit-seats"
          />
        </div>

        {tables.length > 0 && (
          <>
            <div>
              <label className={label} htmlFor="bulk-edit-chair-size">
                Chair Size (ft)
              </label>
              <input
                id="bulk-edit-chair-size"
                type="number"
                min={0.5}
                step={0.25}
                value={chairSizeFt}
                onChange={(e) => setChairSizeFt(parseFloat(e.target.value) || 1)}
                className={field}
                data-testid="bulk-edit-chair-size"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="bulk-always-chairs"
                checked={alwaysPlaceChairs}
                onChange={(e) => setAlwaysPlaceChairs(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300"
              />
              <label htmlFor="bulk-always-chairs" className="text-sm text-gray-700">
                Place chairs on ALL tables (not just those that already have chairs)
              </label>
            </div>
          </>
        )}

        <div>
          <div className="flex items-center gap-2 mb-1">
            <input
              type="checkbox"
              id="bulk-change-label"
              checked={changeLabel}
              onChange={(e) => setChangeLabel(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <label
              htmlFor="bulk-change-label"
              className="text-sm font-medium text-gray-700 cursor-pointer"
            >
              Change Label
            </label>
          </div>
          {changeLabel && (
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="New label for all matching objects"
              className={field}
              data-testid="bulk-edit-label"
              autoFocus
            />
          )}
        </div>

        <div className="bg-gray-50 rounded p-2 text-xs text-gray-600 space-y-0.5">
          <div>
            Will update <strong>{matching.length}</strong> object(s)
          </div>
          {tables.length > 0 && (
            <div>
              {alwaysPlaceChairs
                ? `Will place chairs on all ${tables.length} table(s)`
                : withChairs > 0
                  ? `Will re-place chairs on ${withChairs} table(s) that already have chairs`
                  : 'No tables currently have chairs placed'}
            </div>
          )}
        </div>
      </div>
    </EntityModal>
  );
}
